class SCountryModal extends HTMLElement {
  constructor() {
    super();
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.opener = null;
  }

  connectedCallback() {
    this.input = this.querySelector('[data-country-input]');
    this.current = this.querySelector('[data-country-current]');
    this.toggle = this.querySelector('[data-country-toggle]');
    this.dropdown = this.querySelector('[data-country-dropdown]');
    this.options = this.querySelector('[data-country-options]');
    this.optionsTemplate = this.querySelector('[data-country-template]');
    this.search = this.querySelector('[data-country-search]');
    this.noResults = this.querySelector('[data-country-no-results]');

    /* Owner, 2026-09-28: the language row (I18N-03, #160) is the country control's button and
       list; the hidden input is what Apply posts as language_code. */
    this.langInput = this.querySelector('[data-language-input]');
    this.langToggle = this.querySelector('[data-language-toggle]');
    this.langDropdown = this.querySelector('[data-language-dropdown]');
    this.langOptions = this.querySelector('[data-language-options]');
    this.langCurrent = this.querySelector('[data-language-current]');
    this.langCurrentCode = this.querySelector('[data-language-current-code]');

    /* Owner, 2026-09-28 (spec 2026-09-28-header-region-globe-design.md): the WOOPF header's globe
       and the phone menu's Region tile open this pop-up through the same PubSub bridge as the cart
       drawer and the search dialog (ADR 0005). The element focused when the message arrives (the
       globe, or the burger) gets focus back on close. One subscription, even when a Theme Editor
       reload connects this element again. */
    if (window.PubSub && typeof window.PubSub.subscribe === 'function') {
      this.unsubscribeOpen?.();
      this.unsubscribeOpen = window.PubSub.subscribe('country-modal:open', () => {
        this.opener = document.activeElement;
        this.openerIsIcon = false;
        this.open();
      });
    }

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('.js-country-modal-open');
      if (opener) {
        this.opener = opener;
        /* Owner, 2026-09-28: the legacy icon is this dialog's disclosure, so it reports
           aria-expanded */
        this.openerIsIcon = true;
        this.open();
      }
      if (e.target.closest('[data-country-close]')) this.close();

      const option = e.target.closest('[data-country-option]');
      if (option && this.contains(option)) {
        this.selectCountry(option);
      }

      const languageOption = e.target.closest('[data-language-option]');
      if (languageOption && this.contains(languageOption)) {
        this.selectLanguage(languageOption);
      }

      if (this.classList.contains('is-open') && this.dropdown && !this.dropdown.hidden) {
        const clickedInsideField = e.target.closest('.country-modal__field');
        if (!clickedInsideField && this.contains(e.target)) {
          this.closeDropdown();
        }
      }

      /* The language list closes on a click anywhere else in the modal, as the country list does */
      if (this.classList.contains('is-open') && this.langDropdown && !this.langDropdown.hidden) {
        const clickedInsideLanguage = e.target.closest('.country-modal__language');
        if (!clickedInsideLanguage && this.contains(e.target)) {
          this.closeLanguageDropdown();
        }
      }
    });

    this.toggle?.addEventListener('click', () => {
      this.toggleDropdown();
    });

    this.langToggle?.addEventListener('click', () => {
      this.toggleLanguageDropdown();
    });
    this.langOptions?.addEventListener('keydown', (e) => this.onLanguageKeydown(e));

    this.search?.addEventListener('input', () => {
      this.filterCountries();
    });

    /* A11Y-11 (#290): the 237 countries are one tab stop; the arrow keys move inside the list, and
       ArrowDown from the search box enters it */
    this.options?.addEventListener('keydown', (e) => this.onOptionsKeydown(e));
    this.search?.addEventListener('keydown', (e) => this.onSearchKeydown(e));
    this.syncRoving();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        /* A11Y-11 (#290): mark this Escape handled. The modal can open over the mobile menu, and
           c-dialog.js closes the top dialog that asked it to on an unhandled Escape - the menu -
           so one Escape used to close both. It skips a prevented one. */
        e.preventDefault();
        if (this.langDropdown && !this.langDropdown.hidden) {
          this.closeLanguageDropdown(true);
          return;
        }

        if (this.dropdown && !this.dropdown.hidden) {
          this.closeDropdown();
          return;
        }

        this.close();
      }
    });
  }

  /* Owner, 2026-09-28: the flags are Shopify's images, drawn in the markup by
     snippets/woopf-country-flag.liquid. The emoji this file used to write into every
     [data-country-code] showed as two letters on Windows, and its fallback was a globe emoji. */

  setExpanded(isOpen) {
    this.toggle?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  /* PERF-D01 (#463): the country rows ship inside the <template> in
     sections/s-country-modal.liquid, so until this runs they are not in the document tree and
     nothing styles, lays out or selector-matches them. They are moved in - not cloned - the first
     time the list opens, which is the only moment they can be seen, and the template is dropped
     afterwards so this happens once per page. Their flag images were already lazy, so this is
     about the 1,422 nodes, not about requests.

     filterCountries() and not syncRoving(): it sets the roving tabindex itself and also recomputes
     the empty-state message, which close() would otherwise leave showing. close() calls
     closeDropdown() -> filterCountries() even when the list was never opened, and at that point
     zero visible options reads as "no results". */
  hydrateOptions() {
    if (!this.optionsTemplate || !this.options) return;

    this.options.appendChild(this.optionsTemplate.content);
    this.optionsTemplate.remove();
    this.optionsTemplate = null;
    this.filterCountries();
  }

  toggleDropdown() {
    if (!this.dropdown) return;

    if (this.dropdown.hidden) this.hydrateOptions();

    this.dropdown.hidden = !this.dropdown.hidden;
    this.setExpanded(!this.dropdown.hidden);

    if (!this.dropdown.hidden) {
      /* One list at a time */
      this.closeLanguageDropdown();
      setTimeout(() => {
        this.search?.focus();
      }, 50);
    }
  }

  closeDropdown() {
    if (this.dropdown) {
      this.dropdown.hidden = true;
    }

    this.setExpanded(false);

    if (this.search) {
      this.search.value = '';
    }

    this.filterCountries();
  }

  filterCountries() {
    const query = (this.search?.value || '').trim().toLowerCase();
    const options = Array.from(this.querySelectorAll('[data-country-option]'));
    let visibleCount = 0;

    options.forEach((option) => {
      const value = (option.dataset.countrySearchValue || option.textContent || '').toLowerCase();
      const isVisible = !query || value.includes(query);

      option.hidden = !isVisible;

      if (isVisible) {
        visibleCount += 1;
      }
    });

    if (this.noResults) {
      this.noResults.hidden = visibleCount !== 0;
    }

    /* A11Y-11 (#290): a filter can hide the option that held the tab stop */
    this.syncRoving();
  }

  selectCountry(option) {
    const code = option.dataset.countryCode || '';
    const label = option.dataset.countryLabel || option.textContent.trim();

    if (this.input) {
      this.input.value = code;
    }

    if (this.current) {
      this.current.textContent = label;
    }

    if (this.toggle) {
      this.toggle.dataset.countryCode = code;

      /* The button shows the picked row's flag: a copy of that row's image. */
      const triggerFlag = this.toggle.querySelector('.js-country-flag');
      const optionFlag = option.querySelector('.js-country-flag img');
      if (triggerFlag && optionFlag) {
        triggerFlag.replaceChildren(optionFlag.cloneNode(true));
      }
    }

    this.querySelectorAll('[data-country-option]').forEach((el) => {
      el.classList.remove('is-active');
      el.setAttribute('aria-selected', 'false');
    });

    option.classList.add('is-active');
    option.setAttribute('aria-selected', 'true');
    this.closeDropdown();
  }

  open() {
    /* A11Y-11 (#290), global constraint: a Theme Editor reload can leave a stale instance still
       subscribed to the document click listener; a detached root must never claim to be open or
       drive CDialog against a root that has left the page */
    if (!this.isConnected) return;

    this.hidden = false;
    document.body.classList.add('modal-open');
    /* A11Y-11 (#290): the header icon reports the dialog open. A topic opener (the WOOPF globe or
       burger) is not this dialog's disclosure, so only the legacy icon reports it (owner,
       2026-09-28) */
    if (this.openerIsIcon !== false) this.opener?.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.toggle?.focus();
      /* A11Y-11 (#290): the page behind goes inert while the modal is open (assets/c-dialog.js).
         Focus is already on the toggle, and this modal keeps its own Escape handling. */
      window.CDialog?.open({
        root: this,
        initialFocus: this.toggle,
        returnFocus: this.opener,
        fallbackFocus: this.opener
      });
    });
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('modal-open');
    /* A11Y-11 (#290): report the dialog closed and release the page at once; the timeout below
       still hands focus back to the opener once the modal has faded */
    /* Owner, 2026-09-28: a topic opener never got aria-expanded on open, so it does not get it
       cleared here either */
    if (this.openerIsIcon !== false) this.opener?.setAttribute('aria-expanded', 'false');
    window.CDialog?.close(this, { restoreFocus: false });

    setTimeout(() => {
      this.hidden = true;
      this.closeDropdown();
      this.closeLanguageDropdown();
      /* A11Y-11 (#290), global constraint: send focus back to the icon that opened it, so keyboard
         users do not land at the top of the document. A Theme Editor reload of the header can
         replace that icon while the modal stays open, leaving this.opener detached; look up the
         live one instead of stranding focus on <body>. Strictly false: isConnected is undefined on
         a plain object, which is not detached. */
      const opener =
        this.opener && this.opener.isConnected !== false
          ? this.opener
          : document.querySelectorAll('.js-country-modal-open')[0];
      opener?.focus();
      this.opener = null;
    }, 220);
  }

  /* A11Y-11 (#290): a Theme Editor reload replaces this element; release the page it made inert */
  disconnectedCallback() {
    window.CDialog?.close(this, { restoreFocus: false });
    /* Owner, 2026-09-28: release the country-modal:open subscription */
    this.unsubscribeOpen?.();
    this.unsubscribeOpen = null;
  }

  /* A11Y-11 (#290): the countries the current filter leaves visible */
  visibleOptions() {
    return Array.from(this.querySelectorAll('[data-country-option]')).filter((option) => !option.hidden);
  }

  /* A11Y-11 (#290): exactly one visible option is in the tab order - the selected one while it is
     visible, else the first visible one */
  syncRoving() {
    const visible = this.visibleOptions();
    const current =
      visible.find((option) => option.getAttribute('aria-selected') === 'true') || visible[0] || null;
    this.querySelectorAll('[data-country-option]').forEach((option) => {
      option.setAttribute('tabindex', option === current ? '0' : '-1');
    });
  }

  /* A11Y-11 (#290): move the one tab stop, and focus, to this option */
  focusOption(option) {
    if (!option) return;
    this.querySelectorAll('[data-country-option]').forEach((el) => {
      el.setAttribute('tabindex', el === option ? '0' : '-1');
    });
    option.focus();
  }

  /* A11Y-11 (#290): ArrowDown from the search box enters the list */
  onSearchKeydown(e) {
    if (e.key !== 'ArrowDown') return;
    const first = this.visibleOptions()[0];
    if (!first) return;
    e.preventDefault();
    this.focusOption(first);
  }

  /* A11Y-11 (#290): ArrowUp/ArrowDown step through the visible countries (ArrowUp on the first goes
     back to the search box), Home and End jump to the ends. Enter and Space stay with the button. */
  onOptionsKeydown(e) {
    const visible = this.visibleOptions();
    const current = e.target && e.target.closest ? e.target.closest('[data-country-option]') : null;
    const index = visible.indexOf(current);
    if (index === -1) return;

    let next = null;
    if (e.key === 'ArrowDown') next = visible[Math.min(index + 1, visible.length - 1)];
    else if (e.key === 'ArrowUp') next = index === 0 ? this.search : visible[index - 1];
    else if (e.key === 'Home') next = visible[0];
    else if (e.key === 'End') next = visible[visible.length - 1];
    if (!next) return;

    e.preventDefault();
    if (next === this.search) this.search.focus();
    else this.focusOption(next);
  }

  /* --- The language list (owner, 2026-09-28). It works as the country list does, without a
     search box: five languages need none. --- */

  languageOptions() {
    return Array.from(this.querySelectorAll('[data-language-option]'));
  }

  toggleLanguageDropdown() {
    if (!this.langDropdown) return;

    if (this.langDropdown.hidden) this.openLanguageDropdown();
    else this.closeLanguageDropdown();
  }

  /* Opening it closes the country list and puts focus on the current language */
  openLanguageDropdown() {
    if (!this.langDropdown) return;

    this.closeDropdown();
    this.langDropdown.hidden = false;
    this.langToggle?.setAttribute('aria-expanded', 'true');

    const options = this.languageOptions();
    const current = options.find((option) => option.getAttribute('aria-selected') === 'true') || options[0];
    setTimeout(() => {
      this.focusLanguageOption(current);
    }, 50);
  }

  /* returnFocus: after a pick or Escape, focus goes back to the button rather than to <body> */
  closeLanguageDropdown(returnFocus = false) {
    if (this.langDropdown) {
      this.langDropdown.hidden = true;
    }

    this.langToggle?.setAttribute('aria-expanded', 'false');

    if (returnFocus) {
      this.langToggle?.focus();
    }
  }

  selectLanguage(option) {
    const code = option.dataset.languageCode || '';
    const label = option.querySelector('.country-modal__option-label');
    const chip = option.querySelector('.country-modal__language-code');

    if (this.langInput) {
      this.langInput.value = code;
    }

    if (this.langCurrent) {
      this.langCurrent.textContent = (label ? label.textContent : option.textContent).trim();
      this.langCurrent.setAttribute('lang', code);
    }

    if (this.langCurrentCode && chip) {
      this.langCurrentCode.textContent = chip.textContent.trim();
    }

    this.languageOptions().forEach((el) => {
      el.classList.remove('is-active');
      el.setAttribute('aria-selected', 'false');
      el.setAttribute('tabindex', '-1');
    });

    option.classList.add('is-active');
    option.setAttribute('aria-selected', 'true');
    option.setAttribute('tabindex', '0');
    this.closeLanguageDropdown(true);
  }

  /* One tab stop in the list, moved with focus */
  focusLanguageOption(option) {
    if (!option) return;
    this.languageOptions().forEach((el) => {
      el.setAttribute('tabindex', el === option ? '0' : '-1');
    });
    option.focus();
  }

  /* ArrowUp/ArrowDown step through the languages, Home and End jump to the ends. Enter and Space
     stay with the row's button. */
  onLanguageKeydown(e) {
    const options = this.languageOptions();
    const current = e.target && e.target.closest ? e.target.closest('[data-language-option]') : null;
    const index = options.indexOf(current);
    if (index === -1) return;

    let next = null;
    if (e.key === 'ArrowDown') next = options[Math.min(index + 1, options.length - 1)];
    else if (e.key === 'ArrowUp') next = options[Math.max(index - 1, 0)];
    else if (e.key === 'Home') next = options[0];
    else if (e.key === 'End') next = options[options.length - 1];
    if (!next) return;

    e.preventDefault();
    this.focusLanguageOption(next);
  }
}

if (!customElements.get('s-country-modal')) {
  customElements.define('s-country-modal', SCountryModal);
}
